import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Apis from "../../utils/apis";
import { API_AXIOS } from "../../http/interceptor";
import ModernServerTable from "../../components/ModernServerTable";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { saveLeadReport } from "../../store/reducers/leadReport";
import { customCsvDownload, customExcelDownload } from "../../utils/commonMethods";
import { isAdmin } from "../../utils/menuItems";
import MultiSelect from "../../components/MultiSelect";
import { useGetAssignUsersQuery } from "../../store/services/assigningUsers";
import { useLazyGetLeadReportQuery, useLazyGetReAssignedLeadReportQuery } from "../../store/services/leadReportService";

const ReAssignedLeadsReport = () => {
    const { auth } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [pnlUsers, setPnlUsers] = useState([]);
    const [fetched, setFetched] = useState(false);

    const [filter, setFilter] = useState({
        startDate: null,
        endDate: null,
        sr_managers: []
    });

    const { data: allScopedUsers } = useGetAssignUsersQuery();

    const [triggerLeadReport, { isFetching }] = useLazyGetReAssignedLeadReportQuery();

    useEffect(() => {
        if (allScopedUsers?.data) {
            const pnlUsers = allScopedUsers.data.filter((user) => user?.role === "sr_manager") || [];
            setPnlUsers(pnlUsers);
        }
    }, [allScopedUsers]);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3" />;
        return sortConfig.direction === "asc" ? (
            <ArrowUp className="w-3 h-3" />
        ) : (
            <ArrowDown className="w-3 h-3" />
        );
    };

    const {
        rows: reduxRows = [],
        filter: savedFilter = {},
        fetched: reduxFetched = false,
    } = useSelector((state) => state.leadReport);

    // useEffect(() => {
    //     console.log("LEAD REPORT DATA =>", {
    //         fetched,
    //         filter: savedFilter,
    //         rows: reduxRows,
    //     });
    // }, [fetched, savedFilter, reduxRows]);

    useEffect(() => {
        if (reduxFetched) {
            setRows(reduxRows);
            setFetched(true);

            setFilter((prev) => ({
                ...prev,
                startDate: savedFilter?.startDate ? dayjs(savedFilter.startDate) : null,
                endDate: savedFilter?.endDate ? dayjs(savedFilter.endDate) : null,
                sr_managers: savedFilter?.sr_managers ?? prev.sr_managers,
            }));
        }
    }, [reduxRows, reduxFetched, savedFilter]);

    const handleApply = async () => {
        try {
            setLoading(true);

            const startDate = filter?.startDate
                ? dayjs(filter.startDate).format("YYYY-MM-DD")
                : null;
            const endDate = filter?.endDate
                ? dayjs(filter.endDate).format("YYYY-MM-DD")
                : null;

            const data = await triggerLeadReport({
                startDate: startDate,
                endDate: endDate,
                sr_managers: filter.sr_managers,
            }).unwrap();

            const formattedRows = [];

            (data?.stats || []).forEach((agent, agentIndex) => {
                const projectCount = agent.projects?.length || 1;
                (agent.projects || []).forEach((project, projectIndex) => {
                    formattedRows.push({
                        id: `${agentIndex}-${projectIndex}`,
                        fromUser: projectIndex === 0 ? agent.fromUser : "",
                        fromUserTotalLeads: projectIndex === 0 ? agent.totalLeads : null,
                        rowSpan: projectIndex === 0 ? projectCount : 0,
                        toUser: project.toUser,
                        projectName: project.projectName,
                        totalLeads: project.totalLeads,
                        statuses: project.statusBreakdown || [],
                    });
                });
            });

            dispatch(
                saveLeadReport({
                    filter: {
                        startDate,
                        endDate,
                        sr_managers: filter.sr_managers,
                    },
                    rows: formattedRows,
                    fetched: true,
                }),
            );

            setRows(formattedRows);
            setFetched(true);
        } catch (error) {
            console.error(
                "CURRENT ASSIGNEE API ERROR =>",
                error?.response?.data || error,
            );
        } finally {
            setLoading(false);
        }
    };

    const exportFilteredData = useMemo(() => {
        if (rows && rows.length > 0) {

            return rows.map((item) => ({
                "Original Assignee": item.originalAssignee || "",
                "Assignee Total Leads": item.assigneeTotalLeads ?? "",
                "Current Assignee": item.currentAssignee,
                Project: item.projectName,
                "Project Leads": item.totalLeads,
                "Status Breakdown": (item.statuses || [])
                    .map((s) => `${s.status.replaceAll("_", " ")} (${s.count})`)
                    .join(", "),
            }))
        }

    }, [rows])

    const columns = [
        {
            field: "originalAssignee",
            headerName: "Original Assignee",
            minWidth: 220,
            flex: 1.2,
            className: "border-r border-gray-300",
        },
        {
            field: "currentAssignee",
            headerName: "Current Assignee",
            minWidth: 220,
            flex: 1.2,
            className: "border-r border-gray-300",
        },
        {
            field: "projectName",
            headerName: "Project",
            minWidth: 220,
            flex: 1.2,
            className: "border-r border-gray-300",
        },
        {
            field: "totalLeads",
            headerName: "Total Leads",
            minWidth: 120,
            flex: 0.8,
            className: "border-r border-gray-300",
        },
        {
            field: "statuses",
            headerName: "Status Breakdown",
            minWidth: 350,
            flex: 2,
            renderCell: ({ row }) => (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        py: 1,
                    }}
                >
                    {(row.statuses || []).map((status, index) => (
                        <Typography
                            key={index}
                            sx={{
                                fontSize: 13,
                                lineHeight: 1.8,
                            }}
                        >
                            • {status.status.replaceAll("_", " ")} ({status.count})
                        </Typography>
                    ))}
                </Box>
            ),
        },
    ];

    const tdStyle = {
        padding: "12px 16px",
        fontSize: 13,
        border: "1px solid #e5e7eb",
        textAlign: "center",
        verticalAlign: "middle",
    };

    const headers = [
        { label: "Original Assignee", key: "originalAssignee" },
        { label: "Assignee Total", key: "assigneeTotalLeads" },
        { label: "Current Assignee", key: "currentAssignee" },
        { label: "Project", key: "projectName" },
        { label: "Project Leads", key: "totalLeads" },
        { label: "Status Breakdown", key: null }, // not sortable
    ];

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;

        // We need to sort by assignee groups, not individual rows
        // First, group rows back by originalAssignee order
        const groups = [];
        rows.forEach((row) => {
            if (row.rowSpan > 0) {
                groups.push({ leader: row, children: [] });
            } else {
                groups[groups.length - 1].children.push(row);
            }
        });

        const sorted = [...groups].sort((a, b) => {
            let aVal = a.leader[sortConfig.key] ?? "";
            let bVal = b.leader[sortConfig.key] ?? "";

            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            return sortConfig.direction === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });

        // Flatten back
        return sorted.flatMap(({ leader, children }) => [leader, ...children]);
    }, [rows, sortConfig]);


    const handleExport = () => {
        if (!rows.length) return;

        const dateRange = [
            filter.startDate ? dayjs(filter.startDate).format("DD-MM-YYYY") : "all",
            filter.endDate ? dayjs(filter.endDate).format("DD-MM-YYYY") : "all",
        ].join("_to_");

        const category = filter.leadType || "all";
        const fileName = `call_log_report_${category}_${dateRange}`;

        customExcelDownload(exportFilteredData, [], fileName);
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#111827",
                    }}
                >
                    Lead Report
                </Typography>

                <Typography
                    sx={{
                        fontSize: 13,
                        color: "#6b7280",
                        mt: 0.3,
                    }}
                >
                    View current lead ownership report
                </Typography>
            </Box>

            {/* Filters */}
            <Box
                sx={{
                    background: "#fff",
                    border: "0.5px solid #e5e7eb",
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                }}
            >
                <Grid container spacing={1.5} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                mb: 0.7,
                            }}
                        >
                            Start Date
                        </Typography>
                        <DatePicker
                            value={filter.startDate}
                            onChange={(value) =>
                                setFilter((prev) => ({
                                    ...prev,
                                    startDate: value,
                                    // Reset endDate if it's now invalid
                                    endDate:
                                        prev.endDate && value && prev.endDate.isBefore(value)
                                            ? null
                                            : prev.endDate,
                                }))
                            }
                            sx={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            maxDate={filter.endDate || dayjs()}
                            slotProps={{ field: { size: "small" } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: "#6b7280",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                mb: 0.7,
                            }}
                        >
                            End Date
                        </Typography>

                        <DatePicker
                            value={filter.endDate}
                            onChange={(value) =>
                                setFilter((prev) => ({ ...prev, endDate: value }))
                            }
                            sx={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            minDate={filter.startDate || undefined} // can't pick end before startDate
                            maxDate={dayjs()} // can't pick future dates
                            slotProps={{ field: { size: "small" } }}
                        />
                    </Grid>
                    {isAdmin.includes(auth?.role) && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: "#6b7280",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    mb: 0.7,
                                }}
                            >
                                PNL
                            </Typography>
                            <MultiSelect
                                value={filter.sr_managers}
                                name="sr_managers"
                                handleChange={(key, value) => setFilter((prev) => ({ ...prev, [key]: value }))
                                }
                                items={pnlUsers}
                                canSearch
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleApply}
                            disabled={loading}
                            sx={{
                                height: 40,
                                background: "#111827",
                                color: "#fff",
                                fontWeight: 500,
                                fontSize: 13,
                                textTransform: "none",
                                borderRadius: 2,
                                "&:hover": {
                                    background: "#1f2937",
                                },
                            }}
                        >
                            {loading ? "Loading..." : "Get Report"}
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        {rows.length > 0 && (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleExport}
                                disableElevation
                                sx={{
                                    height: 40,
                                    mt: 0.8,
                                    background: "#1e3a5f",
                                    color: "#fff",
                                    fontWeight: 500,
                                    fontSize: 13,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    "&:hover": { background: "#162d4a" },
                                }}
                                startIcon={
                                    <i className="ti ti-download" style={{ fontSize: 15 }} />
                                }
                            >
                                Export CSV
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {fetched && rows.length > 0 ? (
                <Box
                    sx={{
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
                        <Typography
                            sx={{ color: "#2e67be", fontWeight: 600, fontSize: 15 }}
                        >
                            Lead Report
                        </Typography>
                    </Box>

                    <Box sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f9fbfd" }}>
                                    {headers.map((h) => (
                                        <th
                                            key={h.label}
                                            onClick={() => h.key && requestSort(h.key)}
                                            style={{
                                                padding: "12px 16px",
                                                textAlign: "center",
                                                fontSize: 12,
                                                color: "#6b7280",
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                                border: "1px solid #e5e7eb",
                                                whiteSpace: "nowrap",
                                                cursor: h.key ? "pointer" : "default",
                                                userSelect: "none",
                                                background:
                                                    sortConfig.key === h.key ? "#f0f4ff" : "#f9fbfd",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 6,
                                                }}
                                            >
                                                {h.label}
                                                {h.key && (
                                                    <span
                                                        style={{
                                                            color:
                                                                sortConfig.key === h.key
                                                                    ? "#2e67be"
                                                                    : "#9ca3af",
                                                        }}
                                                    >
                                                        {getSortIcon(h.key)}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((row, index) => (
                                    <tr
                                        key={row.id}
                                        style={{
                                            background: index % 2 === 0 ? "#fff" : "#fafafa",
                                        }}
                                    >
                                        {/* Original Assignee — rowspan */}
                                        {row.rowSpan > 0 && (
                                            <td
                                                rowSpan={row.rowSpan}
                                                style={{
                                                    padding: "12px 16px",
                                                    fontSize: 13,
                                                    border: "1px solid #e5e7eb",
                                                    textAlign: "center",
                                                    fontWeight: 500,
                                                    verticalAlign: "middle",
                                                    background: "#fff",
                                                }}
                                            >
                                                {row.fromUser}
                                            </td>
                                        )}

                                        {/* Assignee Total — rowspan */}
                                        {row.rowSpan > 0 && (
                                            <td
                                                rowSpan={row.rowSpan}
                                                style={{
                                                    padding: "12px 16px",
                                                    fontSize: 13,
                                                    border: "1px solid #e5e7eb",
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                    color: "#2e67be",
                                                    verticalAlign: "middle",
                                                    background: "#fff",
                                                }}
                                            >
                                                {row.fromUserTotalLeads}
                                            </td>
                                        )}

                                        {/* Current Assignee */}
                                        <td style={tdStyle}>{row.toUser}</td>

                                        {/* Project */}
                                        <td style={tdStyle}>{row.projectName}</td>

                                        {/* Project Leads */}
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                                            {row.totalLeads}
                                        </td>

                                        {/* Status Breakdown */}
                                        <td style={{ ...tdStyle, textAlign: "left" }}>
                                            {(row.statuses || []).map((s, i) => (
                                                <div
                                                    key={i}
                                                    style={{ fontSize: 13, lineHeight: "1.8" }}
                                                >
                                                    • {s.status.replaceAll("_", " ")} ({s.count})
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            ) : fetched ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 5,
                    }}
                >
                    <Typography fontSize={22} fontWeight={700}>
                        No Data Found
                    </Typography>

                    <Typography color="#6b7280">
                        No records available for selected filters
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 5,
                    }}
                >
                    <Typography fontSize={22} fontWeight={700}>
                        Apply filter to see the report!
                    </Typography>

                    <Typography color="#6b7280">
                        Select dates and click Get Report
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ReAssignedLeadsReport;
